'use client';

import { useMutation, useQueryClient, queryOptions } from '@tanstack/react-query';

import { QUERY_KEYS } from '@/shared/constants';

import { fetchSession, fetchPermissions, loginUser, logoutUser, refreshSession, switchRole } from './auth-api';
import type { LoginCredentials } from './types';

export const SESSION_QUERY_KEY = [QUERY_KEYS.AUTH_SESSION] as const;

export const sessionQueryOptions = queryOptions({
  queryKey: SESSION_QUERY_KEY,
  queryFn: fetchSession,
  staleTime: 5 * 60 * 1000,
  refetchInterval: 60 * 1000,
  refetchOnWindowFocus: true,
  retry: false,
});

export const PERMISSIONS_QUERY_KEY = [QUERY_KEYS.PERMISSIONS] as const;

export const permissionsQueryOptions = (enabled: boolean) =>
  queryOptions({
    queryKey: PERMISSIONS_QUERY_KEY,
    queryFn: fetchPermissions,
    staleTime: 5 * 60 * 1000,
    enabled,
    retry: false,
  });

export function useLoginMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) => loginUser(credentials),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PERMISSIONS_QUERY_KEY });
    },
  });
}

export function useLogoutMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: logoutUser,
    onSuccess: () => {
      queryClient.setQueryData(SESSION_QUERY_KEY, { authenticated: false });
      queryClient.removeQueries({ queryKey: PERMISSIONS_QUERY_KEY });
    },
  });
}

/** A 401 from the BFF means the session is definitively dead. */
function isUnauthorizedError(error: unknown): boolean {
  return (error as { status?: number } | null)?.status === 401;
}

export function useRefreshMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: refreshSession,
    // Transient failures (network/timeout/503) retry with backoff; a 401 is
    // final — retrying cannot revive a dead session.
    retry: (failureCount, error) =>
      !isUnauthorizedError(error) && failureCount < 2,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    },
    onError: (error) => {
      // On transient errors the server-side session is intact: keep the local
      // auth state so the user is not bounced to /login. The reactive refresh
      // in the BFF proxy routes recovers the session on the next request.
      if (!isUnauthorizedError(error)) return;
      queryClient.setQueryData(SESSION_QUERY_KEY, { authenticated: false });
      queryClient.removeQueries({ queryKey: PERMISSIONS_QUERY_KEY });
    },
  });
}

export function useSwitchRoleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantUuid, roleUuid }: { tenantUuid: string; roleUuid: string }) =>
      switchRole(tenantUuid, roleUuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: PERMISSIONS_QUERY_KEY });
    },
  });
}
